import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { FileText, User, Calendar, Building2, CheckCircle, XCircle, Eye, Clock, Bot, Sparkles, ThumbsUp, ThumbsDown, Lightbulb, Loader2, CloudUpload, FolderOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportReview = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [aiReviewDialogOpen, setAiReviewDialogOpen] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");

  useEffect(() => {
    fetchPendingReports();
  }, []);

  const fetchPendingReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/pending-review`, { withCredentials: true });
      setReports(response.data);
    } catch (error) {
      toast.error("Raporlar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const [reviewLoading, setReviewLoading] = useState(false);
  const [driveUploadStatus, setDriveUploadStatus] = useState(null);

  const handleReview = async (status) => {
    if (!selectedReport) return;
    setReviewLoading(true);
    setDriveUploadStatus(status === "approved" ? "uploading" : null);
    
    try {
      let endpoint;
      if (selectedReport.is_royalcert) {
        endpoint = `${API}/royalcert-audits/${selectedReport.audit_record_id}/review`;
      } else if (selectedReport.is_interactive) {
        endpoint = `${API}/interactive-reports/${selectedReport.report_id}/review`;
      } else {
        endpoint = `${API}/reports/${selectedReport.report_id}/review`;
      }
      
      const response = await axios.patch(endpoint, {
        status,
        review_notes: reviewNotes
      }, { withCredentials: true });
      
      // Check drive upload result
      if (status === "approved" && response.data?.drive_upload_result) {
        setDriveUploadStatus("completed");
        const uploadResult = response.data.drive_upload_result;
        toast.success(
          <div>
            <p className="font-medium">Rapor onaylandı!</p>
            <p className="text-sm">
              {uploadResult.uploaded_files?.length || 0} dosya Drive'a yüklendi
            </p>
            {uploadResult.folder_link && (
              <a 
                href={uploadResult.folder_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                📁 Klasörü Aç
              </a>
            )}
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.success(status === "approved" ? "Rapor onaylandı" : "Rapor reddedildi");
      }
      
      setReviewDialogOpen(false);
      setSelectedReport(null);
      setReviewNotes("");
      setDriveUploadStatus(null);
      fetchPendingReports();
    } catch (error) {
      setDriveUploadStatus("failed");
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setReviewLoading(false);
    }
  };

  const handleCancelApproval = async (report) => {
    if (!window.confirm("Onayı kaldırmak istediğinizden emin misiniz?")) return;
    
    try {
      if (report.is_royalcert) {
        await axios.patch(
          `${API}/royalcert-audits/${report.audit_record_id}/cancel-approval`,
          {},
          { withCredentials: true }
        );
      } else if (report.is_interactive) {
        await axios.patch(
          `${API}/interactive-reports/${report.report_id}/cancel-approval`,
          {},
          { withCredentials: true }
        );
      } else {
        await axios.patch(
          `${API}/reports/${report.report_id}/cancel-approval`,
          {},
          { withCredentials: true }
        );
      }
      toast.success("Onay kaldırıldı");
      fetchPendingReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const openReviewDialog = (report) => {
    setSelectedReport(report);
    setReviewNotes("");
    setReviewDialogOpen(true);
  };

  const handleAIReview = async (report) => {
    setSelectedReport(report);
    setAiReview(null);
    setAiReviewDialogOpen(true);
    setAiLoading(true);
    
    try {
      const response = await axios.post(`${API}/ai/review-report`, {
        report_id: report.report_id
      }, { withCredentials: true });
      setAiReview(response.data);
    } catch (error) {
      toast.error("AI inceleme başarısız: " + (error.response?.data?.detail || "Bilinmeyen hata"));
      setAiReviewDialogOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: "bg-blue-100 text-blue-800",
      under_review: "bg-yellow-100 text-yellow-800"
    };
    const labels = {
      submitted: "Gönderildi",
      under_review: "İnceleniyor"
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status]}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="report-review-page">
      <div>
        <h1 className="text-2xl font-bold text-[#111827]">Rapor Onayı</h1>
        <p className="text-[#4B5563]">Onay bekleyen raporları inceleyin</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : reports.length === 0 ? (
        <Card className="border-[#E8F3EE]">
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
            <p className="text-[#4B5563]">Onay bekleyen rapor bulunmuyor</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => (
            <Card key={report.report_id} className={`border-[#E8F3EE] hover:shadow-lg transition-all ${report.has_conflict ? 'opacity-70 border-orange-300' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      report.has_conflict ? 'bg-orange-100' : report.is_royalcert ? 'bg-orange-100' : 'bg-[#1A4D2E]/10'
                    }`}>
                      <FileText className={`w-5 h-5 ${report.has_conflict ? 'text-orange-600' : report.is_royalcert ? 'text-orange-600' : 'text-[#1A4D2E]'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-base text-[#111827]">
                        {report.title || report.customer_name}
                      </CardTitle>
                      <p className="text-xs text-[#9CA3AF] mt-0.5">
                        {report.is_royalcert ? 'RoyalCert Denetim' : report.audit_title}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {getStatusBadge(report.status)}
                    {report.has_conflict && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-700">
                        ⚠️ {report.conflict_reason}
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-[#4B5563]">
                    <Building2 className="w-4 h-4 text-[#9CA3AF]" />
                    {report.customer_name}
                  </div>
                  <div className="flex items-center gap-2 text-[#4B5563]">
                    <User className="w-4 h-4 text-[#9CA3AF]" />
                    {report.created_by_name}
                  </div>
                  <div className="flex items-center gap-2 text-[#4B5563]">
                    <Calendar className="w-4 h-4 text-[#9CA3AF]" />
                    {format(new Date(report.created_at), "dd MMM yyyy", { locale: tr })}
                  </div>
                </div>

                {report.has_conflict ? (
                  <div className="p-3 rounded-lg bg-orange-50 border border-orange-200">
                    <p className="text-sm text-orange-700 font-medium">Çıkar Çatışması</p>
                    <p className="text-xs text-orange-600 mt-1">
                      Bu denetimi {report.conflict_reason?.toLowerCase()} olduğunuz için onaylayamazsınız.
                    </p>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        if (report.is_royalcert) {
                          navigate(`/royalcert-audit/${report.audit_record_id}`);
                        } else if (report.is_interactive) {
                          navigate(`/interactive-report/${report.report_id}`);
                        } else {
                          navigate(`/reports/${report.report_id}`);
                        }
                      }}
                      data-testid={`view-report-${report.report_id || report.audit_record_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      İncele
                    </Button>
                    {report.status === "approved" && user?.role === "super_admin" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                        onClick={() => handleCancelApproval(report)}
                        data-testid={`cancel-approval-${report.report_id || report.audit_record_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Onayı Kaldır
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="flex-1 bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                        onClick={() => openReviewDialog(report)}
                        data-testid={`review-report-${report.report_id || report.audit_record_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Değerlendir
                      </Button>
                    )}
                  </div>
                )}
                {/* AI İnceleme - Şimdilik devre dışı
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2 border-purple-200 text-purple-600 hover:bg-purple-50"
                  onClick={() => handleAIReview(report)}
                  data-testid={`ai-review-${report.report_id}`}
                >
                  <Bot className="w-4 h-4 mr-1" />
                  AI ile İncele
                </Button>
                */}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rapor Değerlendirmesi</DialogTitle>
            <DialogDescription>
              {selectedReport?.title} - {selectedReport?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="review_notes">İnceleme Notları</Label>
              <Textarea
                id="review_notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Değerlendirme notlarınızı yazın..."
                rows={4}
                data-testid="review-notes-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => handleReview("rejected")}
                disabled={reviewLoading}
                data-testid="reject-report-btn"
              >
                {reviewLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                Reddet
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => handleReview("approved")}
                disabled={reviewLoading}
                data-testid="approve-report-btn"
              >
                {reviewLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {reviewLoading && driveUploadStatus === "uploading" ? "Drive'a Yükleniyor..." : "Onayla"}
              </Button>
            </div>
            
            {/* Drive Upload Status */}
            {driveUploadStatus && (
              <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 ${
                driveUploadStatus === "uploading" ? "bg-blue-50 border border-blue-200" :
                driveUploadStatus === "completed" ? "bg-green-50 border border-green-200" :
                "bg-red-50 border border-red-200"
              }`}>
                {driveUploadStatus === "uploading" && (
                  <>
                    <CloudUpload className="w-5 h-5 text-blue-600 animate-pulse" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">PDF'ler Google Drive'a yükleniyor...</p>
                      <p className="text-xs text-blue-600">Lütfen bekleyin, bu işlem birkaç saniye sürebilir</p>
                    </div>
                  </>
                )}
                {driveUploadStatus === "completed" && (
                  <>
                    <FolderOpen className="w-5 h-5 text-green-600" />
                    <p className="text-sm font-medium text-green-800">Dosyalar başarıyla yüklendi!</p>
                  </>
                )}
                {driveUploadStatus === "failed" && (
                  <>
                    <XCircle className="w-5 h-5 text-red-600" />
                    <p className="text-sm font-medium text-red-800">Drive yüklemesi başarısız</p>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Review Dialog */}
      <Dialog open={aiReviewDialogOpen} onOpenChange={setAiReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              AI Rapor İncelemesi
            </DialogTitle>
            <DialogDescription>
              {selectedReport?.title} - {selectedReport?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          {aiLoading ? (
            <div className="py-12 text-center">
              <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#4B5563]">Rapor AI tarafından inceleniyor...</p>
              <p className="text-xs text-[#9CA3AF] mt-1">Bu işlem birkaç saniye sürebilir</p>
            </div>
          ) : aiReview && (
            <div className="space-y-6 mt-4">
              {/* Score */}
              {aiReview.score !== null && (
                <div className="text-center p-4 rounded-xl bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100">
                  <p className="text-sm text-[#4B5563] mb-1">Genel Puan</p>
                  <p className={`text-4xl font-bold ${
                    aiReview.score >= 80 ? 'text-green-600' : 
                    aiReview.score >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {aiReview.score}/100
                  </p>
                </div>
              )}

              {/* Review Text */}
              <div className="p-4 rounded-lg bg-[#F9FAFB]">
                <h4 className="font-medium text-[#111827] mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Genel Değerlendirme
                </h4>
                <p className="text-[#4B5563] text-sm whitespace-pre-wrap">{aiReview.review}</p>
              </div>

              {/* Strengths */}
              {aiReview.strengths?.length > 0 && (
                <div className="p-4 rounded-lg bg-green-50 border border-green-100">
                  <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                    <ThumbsUp className="w-4 h-4" />
                    Güçlü Yönler
                  </h4>
                  <ul className="space-y-2">
                    {aiReview.strengths.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-green-700">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weaknesses */}
              {aiReview.weaknesses?.length > 0 && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-100">
                  <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                    <ThumbsDown className="w-4 h-4" />
                    Zayıf Yönler
                  </h4>
                  <ul className="space-y-2">
                    {aiReview.weaknesses.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-red-700">
                        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Suggestions */}
              {aiReview.suggestions?.length > 0 && (
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                  <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Öneriler
                  </h4>
                  <ul className="space-y-2">
                    {aiReview.suggestions.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-blue-700">
                        <span className="font-medium">{idx + 1}.</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => setAiReviewDialogOpen(false)}
                  className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                >
                  Kapat
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportReview;
